import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  Cpu,
  Database,
  HardDrive,
  Timer,
} from 'lucide-react';
import { AlertActionBanner } from '../../dashboard/AlertActionBanner';
import { fetchSnapshot, fetchTimeseries } from './api';
import { FilterChips } from './FilterChips';
import { formatCount, formatMs, formatPct } from './formatHealth';
import { HealthAreaChart } from './HealthAreaChart';
import { HealthError, HealthPageHeader, HealthSkeleton } from './HealthPageHeader';
import { MetricTile } from './MetricTile';
import { ServiceStrip } from './ServiceStrip';
import type { ChartMetric, ResourceFilter, TimeWindow } from './types';
import { useLivePoll } from './useLivePoll';

const WINDOW_OPTIONS: { id: TimeWindow; label: string }[] = [
  { id: '1h', label: '1h' },
  { id: '6h', label: '6h' },
  { id: '24h', label: '24h' },
  { id: '7d', label: '7d' },
];

const RESOURCE_OPTIONS: { id: ResourceFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'database', label: 'Database' },
  { id: 'compute', label: 'Compute' },
];

const CHART_OPTIONS: { id: ChartMetric; label: string }[] = [
  { id: 'cpu', label: 'CPU' },
  { id: 'memory', label: 'Memory' },
  { id: 'disk', label: 'Disk' },
  { id: 'connections', label: 'Connections' },
];

export const SystemHealthOverlay: React.FC = () => {
  const navigate = useNavigate();
  const [windowKey, setWindowKey] = useState<TimeWindow>('1h');
  const [resource, setResource] = useState<ResourceFilter>('all');
  const [chartMetric, setChartMetric] = useState<ChartMetric>('cpu');
  const [tabVisible, setTabVisible] = useState(
    () => typeof document === 'undefined' || !document.hidden,
  );

  React.useEffect(() => {
    const onVis = () => setTabVisible(!document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  const onUnauth = useCallback(() => {
    sessionStorage.removeItem('portal_token');
    sessionStorage.removeItem('portal_user');
    navigate('/admin/login', { replace: true });
  }, [navigate]);

  const snapshotFetcher = useCallback(
    (signal: AbortSignal) => fetchSnapshot(windowKey, signal),
    [windowKey],
  );
  const seriesFetcher = useCallback(
    (signal: AbortSignal) =>
      fetchTimeseries({ resource: 'database', metric: chartMetric, window: windowKey }, signal),
    [chartMetric, windowKey],
  );

  const snapshot = useLivePoll({
    intervalMs: 15_000,
    fetcher: snapshotFetcher,
    onUnauthenticated: onUnauth,
    resetKey: windowKey,
  });
  const series = useLivePoll({
    enabled: resource !== 'compute',
    intervalMs: 60_000,
    fetcher: seriesFetcher,
    onUnauthenticated: onUnauth,
    resetKey: `${windowKey}:${chartMetric}`,
  });

  const showDatabase = resource === 'all' || resource === 'database';
  const showCompute = resource === 'all' || resource === 'compute';

  const chartPoints = useMemo(() => {
    if (series.data?.points?.length) return series.data.points;
    return snapshot.data?.sparklines?.[chartMetric] || [];
  }, [chartMetric, series.data, snapshot.data]);

  if (snapshot.loading && !snapshot.data) return <HealthSkeleton />;
  if (snapshot.error && !snapshot.data) {
    return <HealthError message={snapshot.error} onRetry={() => snapshot.reload()} />;
  }
  if (!snapshot.data) return null;

  const { data } = snapshot;
  const conn = data.kpis.connections;
  const connValue =
    conn.current != null && conn.max != null
      ? `${formatCount(conn.current)} / ${formatCount(conn.max)}`
      : formatCount(conn.current);

  return (
    <div className="space-y-6">
      <HealthPageHeader
        title="Platform Health"
        subtitle="Live Cloud SQL System Insights and Cloud Run from this environment."
        status={data.overall.status}
        reason={data.overall.reason}
        environment={data.environment}
        region={data.region}
        instanceId={data.database.instanceId}
        generatedAt={data.generatedAt}
        live={tabVisible}
        refreshing={snapshot.refreshing}
        onRefresh={() => {
          void snapshot.reload(true);
          void series.reload(true);
        }}
        consoleUrl={data.database.consoleUrl}
      />

      {data.stale || !data.gcpAvailable ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {!data.gcpAvailable
            ? 'Cloud Monitoring is not reachable here. Database process stats still update.'
            : 'Showing slightly stale GCP samples. A refresh is in progress.'}
        </div>
      ) : null}

      <AlertActionBanner alerts={data.alerts} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <FilterChips label="Window" value={windowKey} options={WINDOW_OPTIONS} onChange={setWindowKey} />
        <FilterChips label="Show" value={resource} options={RESOURCE_OPTIONS} onChange={setResource} />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {showDatabase ? (
          <>
            <MetricTile
              label="CPU"
              value={formatPct(data.kpis.cpuPercent)}
              footnote={data.database.tier || 'Cloud SQL'}
              icon={Cpu}
              accent="green"
              onClick={() => navigate('/admin/system/database')}
            />
            <MetricTile
              label="Memory"
              value={formatPct(data.kpis.memoryPercent)}
              footnote="Instance utilisation"
              icon={Activity}
              accent="blue"
              onClick={() => navigate('/admin/system/database')}
            />
            <MetricTile
              label="Disk"
              value={formatPct(data.kpis.diskPercent)}
              footnote={data.database.lastBackupAt ? `Last backup ${new Date(data.database.lastBackupAt).toLocaleString('en-ZA', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}` : 'Backup age unknown'}
              icon={HardDrive}
              accent="blue"
              onClick={() => navigate('/admin/system/database')}
            />
            <MetricTile
              label="Connections"
              value={connValue}
              footnote={conn.percent != null ? `${formatPct(conn.percent)} of max` : 'PostgreSQL backends'}
              icon={Database}
              accent="green"
              onClick={() => navigate('/admin/system/database')}
            />
          </>
        ) : null}
        {showCompute ? (
          <>
            <MetricTile
              label="API p95"
              value={formatMs(data.kpis.apiP95Ms)}
              footnote="Cloud Run request latency"
              icon={Timer}
              accent="blue"
              onClick={() => navigate('/admin/system/services')}
            />
            <MetricTile
              label="5xx rate"
              value={formatPct(data.kpis.errorRatePercent, 2)}
              footnote={`${formatPct(data.kpis.availability24hPercent, 2)} database uptime (24h)`}
              icon={AlertTriangle}
              accent="green"
              onClick={() => navigate('/admin/system/services')}
            />
          </>
        ) : null}
      </div>

      {showDatabase ? (
        <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-5 shadow-[var(--portal-shadow)]">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#2D8CCA]/10">
                <Activity className="h-5 w-5 text-[var(--mymoolah-blue)]" aria-hidden />
              </div>
              <div>
                <h2 className="text-base font-semibold text-[var(--foreground)]">System Insights</h2>
                <p className="text-xs text-[var(--muted-foreground)]">
                  {data.database.connectionName}
                </p>
              </div>
            </div>
            <FilterChips label="Metric" value={chartMetric} options={CHART_OPTIONS} onChange={setChartMetric} />
          </div>
          <HealthAreaChart
            points={chartPoints}
            unit={chartMetric === 'connections' ? 'count' : 'percent'}
            windowKey={windowKey}
            color={chartMetric === 'cpu' ? '#86BE41' : '#2D8CCA'}
            emptyLabel={data.gcpAvailable ? 'No samples in this window yet' : 'GCP metrics unavailable in this environment'}
          />
        </div>
      ) : null}

      {showCompute ? <ServiceStrip services={data.services} /> : null}
    </div>
  );
};
